import { useTranslation } from 'react-i18next';
import type { InventoryTrendChartKind } from '../../domain/statisticsInventory';
import type { ChartData } from './chartConfigUtils';
import StandardChart from './StandardChart';

type InventoryTrendUnit = 'grams' | 'items' | 'currency' | 'percentage';

interface InventoryTrendChartConfig {
    readonly chartType: 'column' | 'line';
    readonly yAxisTitleKey: string;
    readonly yAxisTitleFallback: string;
    readonly seriesNameKey: string;
    readonly seriesNameFallback: string;
    readonly color: string;
    readonly unit: InventoryTrendUnit;
}

const CHART_CONFIG: Record<InventoryTrendChartKind, InventoryTrendChartConfig> = {
    silverQuantity: {
        chartType: 'column',
        yAxisTitleKey: 'statistics.inventory.silverQuantityAxis',
        yAxisTitleFallback: 'Pure silver (Ag1000 grams)',
        seriesNameKey: 'statistics.inventory.silverQuantity',
        seriesNameFallback: 'Silver inventory quantity',
        color: '#8c8c8c',
        unit: 'grams',
    },
    silverValue: {
        chartType: 'line',
        yAxisTitleKey: 'statistics.inventory.valueAxis',
        yAxisTitleFallback: 'Inventory value',
        seriesNameKey: 'statistics.inventory.silverValue',
        seriesNameFallback: 'Silver inventory value',
        color: '#595959',
        unit: 'currency',
    },
    nonMetalQuantity: {
        chartType: 'column',
        yAxisTitleKey: 'statistics.inventory.nonMetalQuantityAxis',
        yAxisTitleFallback: 'Items moved to stock',
        seriesNameKey: 'statistics.inventory.nonMetalQuantity',
        seriesNameFallback: 'Electronics + other inventory quantity',
        color: '#722ed1',
        unit: 'items',
    },
    nonMetalValue: {
        chartType: 'line',
        yAxisTitleKey: 'statistics.inventory.valueAxis',
        yAxisTitleFallback: 'Inventory value',
        seriesNameKey: 'statistics.inventory.nonMetalValue',
        seriesNameFallback: 'Electronics + other inventory value',
        color: '#531dab',
        unit: 'currency',
    },
    expiredPercentage: {
        chartType: 'line',
        yAxisTitleKey: 'statistics.inventory.percentageAxis',
        yAxisTitleFallback: 'Expired value / signed value (%)',
        seriesNameKey: 'statistics.inventory.expiredPercentage',
        seriesNameFallback: 'Expired value as % of signed value',
        color: '#cf1322',
        unit: 'percentage',
    },
};

interface InventoryTrendChartProps {
    readonly data: readonly ChartData[];
    readonly kind: InventoryTrendChartKind;
    readonly loading?: boolean;
    readonly referenceValue?: number;
    readonly currency?: 'EUR' | 'LEI';
}

function valueSuffix(
    unit: InventoryTrendUnit,
    currency: 'EUR' | 'LEI',
    translate: (key: string, fallback: string) => string,
): string {
    if (unit === 'grams') return translate('statistics.grams', ' g');
    if (unit === 'items') return translate('statistics.inventory.itemsSuffix', ' items');
    if (unit === 'percentage') return '%';
    if (currency === 'EUR') return translate('statistics.currencyEur', ' EUR');
    return translate('statistics.currencyLei', ' Lei');
}

function InventoryTrendChart({
    data,
    kind,
    loading = false,
    referenceValue,
    currency = 'EUR',
}: InventoryTrendChartProps): JSX.Element {
    const { t } = useTranslation();
    const config = CHART_CONFIG[kind];

    return (
        <StandardChart
            data={data}
            loading={loading}
            referenceValue={referenceValue}
            config={{
                chartType: config.chartType,
                yAxisTitleKey: config.yAxisTitleKey,
                yAxisTitleFallback: config.yAxisTitleFallback,
                yAxisColor: config.color,
                seriesNameKey: config.seriesNameKey,
                seriesNameFallback: config.seriesNameFallback,
                seriesColor: config.color,
                valueSuffix: valueSuffix(config.unit, currency, t),
            }}
        />
    );
}

export default InventoryTrendChart;
